import { Component, OnInit, ViewChild } from '@angular/core';
import { GridComponent } from 'src/app/core/widgets/grid/grid.component';
import { GridColumn } from 'src/app/core/models/grid-column.model';
import { GridFilter } from 'src/app/core/models/grid-filter.model';
import { GridSearchFilter } from 'src/app/core/models/grid-search-filter.model';
import { ActivatedRoute } from '@angular/router';
import { take, catchError } from 'rxjs/operators';
import moment from 'moment';
import { Product } from 'src/app/order/models/product.model';
import { ProductService } from 'src/app/quoting/services/product.service';
import { Warehouse } from '../../models/warehouse.model';
import { WarehouseLocation } from '../../models/warehouse-location.model';
import { WarehouseService } from '../../services/warehouse.service';
import { WarehouseLocationService } from '../../services/warehouse-location.service';
import { StockQuantityService } from '../../services/stock-quantity.service';

@Component({
    selector: 'app-stock-levels',
    templateUrl: './stock-levels.component.html',
    styleUrls: ['./stock-levels.component.scss'],
    standalone: false
})
export class StockLevelsComponent implements OnInit {

  // Grid reads this.filters when it queries but has no ngOnChanges, so changing
  // a selection has to ask it to re-query.
  @ViewChild(GridComponent)
  grid: GridComponent;

  warehouses: Warehouse[] = [];
  locations: WarehouseLocation[] = [];
  products: Product[] = [];

  selectedWarehouseId: number = -1;
  selectedLocationId: number = -1;
  selectedProductId: number = -1;

  columns: GridColumn[] = [
    {active: true, label: '#', field: 'id'} as GridColumn,
    {active: true, label: 'Product', field: 'product.name'} as GridColumn,
    {active: true, label: 'SKU', field: 'product.sku'} as GridColumn,
    {active: true, label: 'Warehouse', field: 'warehouseLocation.warehouse.name'} as GridColumn,
    {active: true, label: 'Aisle', field: 'warehouseLocation.aisle'} as GridColumn,
    {active: true, label: 'Shelf', field: 'warehouseLocation.shelf'} as GridColumn,
    // Grid blanks a falsy value, so a level sitting at zero is rendered as a string.
    {active: true, label: 'Qty', field: 'qty', value: v => this.stockQuantityService.round(v).toString()} as GridColumn,
    {active: true, label: 'Updated At', field: 'updatedAt', value: v => moment(v).format('DD/MM/YYYY HH:mm:ss')} as GridColumn
  ];

  relationships: string[] = ['product', 'warehouseLocation', 'warehouseLocation.warehouse'];

  filters: GridFilter[] = []

  searchFields: any = ['product.name','product.sku','warehouseLocation.aisle','warehouseLocation.shelf','warehouseLocation.warehouse.name'];

  searchFilters: GridSearchFilter[] = [
    {
      field: 'product.name',
      label: 'Product',
    } as GridSearchFilter,
    {
      field: 'product.sku',
      label: 'SKU',
    } as GridSearchFilter,
    {
      field: 'warehouseLocation.warehouse.name',
      label: 'Warehouse',
    } as GridSearchFilter,
    {
      field: 'warehouseLocation.aisle',
      label: 'Aisle',
    } as GridSearchFilter,
    {
      field: 'warehouseLocation.shelf',
      label: 'Shelf',
    } as GridSearchFilter,
  ]

  constructor(private warehouseService: WarehouseService,
    private warehouseLocationService: WarehouseLocationService,
    private productService: ProductService,
    private stockQuantityService: StockQuantityService,
    private route: ActivatedRoute) { }

  ngOnInit(): void {
    this.loadWarehouses();
    this.loadProducts();

    // The products grid links straight here for one product.
    this.route.queryParams
    .pipe(take(1))
    .subscribe((params) => {
      if (params['productId']) {
        this.onProductChanged(+params['productId']);
      }
    })
  }

  loadWarehouses() {
    this.warehouseService.getAllWarehouses()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load warehouses");
      return e;
    }))
    .subscribe((data: Warehouse[]) => {
      this.warehouses = data;
    })
  }

  loadProducts() {
    this.productService.getAllActiveProducts()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load products");
      return e;
    }))
    .subscribe((data: Product[]) => {
      this.products = data;
    })
  }

  loadLocations() {
    this.warehouseLocationService.getWarehouseLocationsForWarehouse(this.selectedWarehouseId)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load warehouse locations");
      return e;
    }))
    .subscribe((data: WarehouseLocation[]) => {
      this.locations = data;
    })
  }

  onWarehouseChanged(warehouseId: number) {
    this.selectedWarehouseId = +warehouseId;

    // The location list is scoped to the warehouse, so a location picked at the old
    // site no longer applies.
    this.selectedLocationId = -1;
    this.locations = [];

    if (this.selectedWarehouseId !== -1) {
      this.loadLocations();
    }

    this.applyFilters();
  }

  onLocationChanged(locationId: number) {
    this.selectedLocationId = +locationId;
    this.applyFilters();
  }

  onProductChanged(productId: number) {
    this.selectedProductId = +productId;
    this.applyFilters();
  }

  /**
   * A location already belongs to one warehouse, so filtering on the location alone is
   * enough once one is picked.
   */
  private applyFilters() {
    const filters: GridFilter[] = [];

    if (this.selectedLocationId !== -1) {
      filters.push({field: 'warehouseLocationId', condition: 'eq', value: this.selectedLocationId} as GridFilter);
    } else if (this.selectedWarehouseId !== -1) {
      filters.push({field: 'warehouseLocation.warehouseId', condition: 'eq', value: this.selectedWarehouseId} as GridFilter);
    }

    if (this.selectedProductId !== -1) {
      filters.push({field: 'productId', condition: 'eq', value: this.selectedProductId} as GridFilter);
    }

    this.filters = filters;

    this.reloadGrid();
  }

  private reloadGrid() {
    if (!this.grid) {
      return;
    }

    this.grid.filters = this.filters;
    this.grid.paginationService.setPage(1);
    this.grid.newPage = 1;
    this.grid.getRecordsForEntity();
  }
}
